<template>
    <div class="dashboard-container">
        <!-- 顶部筛选区域 -->
        <div class="filter-section">
            <div class="filter-card">
                <div class="filter-content">
                    <div class="filter-item">
                        <span class="filter-label">车牌号筛选：</span>
                        <el-input v-model="filterPlateNumber" placeholder="请输入车牌号" clearable class="tech-input" />
                    </div>
                    <div class="filter-item">
                        <el-button type="primary" class="tech-btn query-btn" @click="handleFilter">
                            <i class="el-icon-search"></i>
                            查询
                        </el-button>
                        <el-button class="tech-btn reset-btn" @click="resetFilter">
                            <i class="el-icon-refresh"></i>
                            重置
                        </el-button>
                    </div>
                </div>
            </div>
        </div>
        <!-- 数据加载状态 -->
        <div v-if="isLoading" class="loading-overlay">
            <div class="loading-spinner">
                <i class="el-icon-loading"></i>
                <p>数据加载中...</p>
            </div>
        </div>
        <!-- 地图和信息面板区域 -->
        <div class="main-content">
            <!-- 地图容器 -->
            <div class="map-container" :class="{ 'map-container-fullscreen': isMapFullscreen }">
                <div id="map-container" class="map"></div>
                <div class="map-tool-panel">
                    <button type="button" class="map-tool-btn" @click="toggleMapFullscreen">
                        {{ isMapFullscreen ? '退出全屏' : '地图全屏' }}
                    </button>
                    <button type="button" class="map-tool-btn" :class="{ active: trafficVisible }" @click="toggleTrafficLayer">
                        路况
                    </button>
                    <button type="button" class="map-tool-btn" :class="{ active: rulerActive }" @click="toggleRuler">
                        测距
                    </button>
                </div>
            </div>

            <!-- 右侧信息面板 -->
            <div class="info-panel">
                <!-- 车辆列表 -->
                <div class="vehicle-list-card">
                    <div class="panel-header">
                        <span>车辆列表</span>
                        <span class="vehicle-count">({{ vehicles.length }})</span>
                    </div>
                    <div ref="vehicleList" class="list-content" @scroll="handleVehicleListScroll">
                        <div v-if="shouldVirtualizeVehicles" :style="{ height: listPaddingTop + 'px' }"></div>
                        <div v-for="vehicle in visibleVehicles" :key="vehicle.id || vehicle.CARCODE" class="vehicle-item"
                            :class="{ active: selectedVehicle && selectedVehicle.id === vehicle.id }"
                            @click="selectVehicle(vehicle)">
                            <div class="vehicle-main">
                                <div class="vehicle-info">
                                    <div class="vehicle-plate">{{ vehicle.CARCODE }}</div>
                                    <div class="vehicle-status" v-if="vehicle.alarmtype">
                                        <div class="status-dot" :class="vehicle.alarmtype"></div>
                                        <el-tag size="mini" class="status-tag">
                                            {{ vehicle.alarmtype }}
                                        </el-tag>
                                    </div>
                                </div>
                                <div class="vehicle-location">{{ vehicle.newaddress }}</div>
                            </div>
                        </div>
                        <div v-if="shouldVirtualizeVehicles" :style="{ height: listPaddingBottom + 'px' }"></div>
                    </div>
                </div>
            </div>
        </div>

        <!-- 车辆信息弹窗 -->
        <el-dialog :visible.sync="showVehicleDialog" title="车辆详细信息" width="600px" class="vehicle-dialog"
            :close-on-click-modal="false" @close="handleDialogClose" append-to-body custom-class="data-board-map-board-dialog">
            <div v-if="selectedVehicle" class="vehicle-detail">
                <div class="detail-section">
                    <h3 class="section-title">基本信息</h3>
                    <div class="detail-grid">
                        <div class="detail-item">
                            <span class="detail-label">车牌号：</span>
                            <span class="detail-value">{{ selectedVehicle.CARCODE }}</span>
                        </div>
                        <div class="detail-item">
                            <span class="detail-label">车辆状态：</span>
                            <span>{{ selectedVehicle.alarmtype || '--' }}</span>
                        </div>
                        <div class="detail-item">
                            <span class="detail-label">SIM卡号：</span>
                            <span class="detail-value">{{ selectedVehicle.SIM }}</span>
                        </div>
                        <div class="detail-item">
                            <span class="detail-label">GPS编号：</span>
                            <span class="detail-value">{{ selectedVehicle.GPSCODE }}</span>
                        </div>
                        <div class="detail-item">
                            <span class="detail-label">所属企业：</span>
                            <span class="detail-value">{{ selectedVehicle.Company }}</span>
                        </div>
                    </div>
                </div>

                <div class="detail-section">
                    <h3 class="section-title">位置信息</h3>
                    <div class="detail-grid">
                        <div class="detail-item">
                            <span class="detail-label">当前位置：</span>
                            <span class="detail-value">{{ selectedVehicle.newaddress }}</span>
                        </div>
                        <div class="detail-item">
                            <span class="detail-label">上线状态：</span>
                            <span class="detail-value">
                                上线
                            </span>
                        </div>
                        <div class="detail-item">
                            <span class="detail-label">最新定位时间：</span>
                            <span class="detail-value">{{ selectedVehicle.t }}</span>
                        </div>
                        <div class="detail-item">
                            <span class="detail-label">总里程：</span>
                            <span class="detail-value">{{ selectedVehicle.mileage }} km</span>
                        </div>
                        <div class="detail-item">
                            <span class="detail-label">当前速度：</span>
                            <span class="detail-value">{{ selectedVehicle.speed }} km/h</span>
                        </div>
                    </div>
                </div>
            </div>
            <div slot="footer" class="dialog-footer">
                <el-button @click="closeDialog" class="tech-btn">关闭</el-button>
                <el-button type="primary" class="tech-btn" @click="closeDialog">确定</el-button>
            </div>
        </el-dialog>
    </div>
</template>

<script>
export default {
    name: 'VehicleMapDashboard',
    data() {
        return {
            map: null, // 地图实例
            markers: [], // 标记点数组
            labelMarkers: [],
            labelsLayer: null,
            markerPositions: [],
            filterPlateNumber: '', // 车牌号筛选
            selectedVehicle: null, // 选中的车辆
            showVehicleDialog: false, // 控制弹窗显示
            // 模拟车辆数据
            vehicles: [],
            mapLoaded: false, // 地图是否加载完成
            isLoading: false, // 是否正在加载
            hasAdjustedMapView: false, // 是否已完成首次视野适配
            performanceMode: document.documentElement.classList.contains('large-screen-perf'),
            listScrollTop: 0,
            listItemHeight: 73,
            visibleListSize: 18,
            roadSnapper: null,
            roadSnapVersion: 0,
            roadSnapCache: Object.create(null),
            markerRenderRaf: null,
            vehiclePositionMap: {},
            mapResizeHandler: null,
            mapActive: true,
            pendingMarkerRender: false,
            markersHiddenForInteraction: false,
            mapInteractionTimer: null,
            mapInteractionEventsBound: false,
            isMapFullscreen: false,
            trafficLayer: null,
            trafficVisible: false,
            ruler: null,
            rulerActive: false,
            rulerLoading: false,
            fullscreenKeyHandler: null,
            heavyVehicleThreshold: 120,
            virtualListThreshold: 80
        }
    },
    computed: {
        shouldVirtualizeVehicles() {
            return this.performanceMode || this.vehicles.length > this.virtualListThreshold
        },
        visibleVehicles() {
            if (!this.shouldVirtualizeVehicles) return this.vehicles
            return this.vehicles.slice(this.visibleStartIndex, this.visibleEndIndex)
        },
        visibleStartIndex() {
            if (!this.shouldVirtualizeVehicles) return 0
            return Math.max(Math.floor(this.listScrollTop / this.listItemHeight) - 3, 0)
        },
        visibleEndIndex() {
            if (!this.shouldVirtualizeVehicles) return this.vehicles.length
            return Math.min(this.visibleStartIndex + this.visibleListSize, this.vehicles.length)
        },
        listPaddingTop() {
            return this.shouldVirtualizeVehicles ? this.visibleStartIndex * this.listItemHeight : 0
        },
        listPaddingBottom() {
            if (!this.shouldVirtualizeVehicles) return 0
            return Math.max((this.vehicles.length - this.visibleEndIndex) * this.listItemHeight, 0)
        }
    },
    mounted() {
        this.mapActive = true
        this.refreshPerformanceMode()
        this.mapResizeHandler = () => {
            this.refreshPerformanceMode()
            if (this.map && typeof this.map.resize === 'function') {
                this.map.resize()
            }
        }
        window.addEventListener('resize', this.mapResizeHandler)
        this.fullscreenKeyHandler = event => {
            if (event.key === 'Escape' && this.isMapFullscreen) {
                this.isMapFullscreen = false
                this.resizeMapNextTick()
            }
        }
        window.addEventListener('keydown', this.fullscreenKeyHandler)
        this.initMap();
    },
    activated() {
        this.mapActive = true
        this.refreshPerformanceMode()
        if (this.map && typeof this.map.resize === 'function') {
            this.resizeMapNextTick()
        }
        if (this.pendingMarkerRender && this.mapLoaded) {
            this.pendingMarkerRender = false
            this.addVehicleMarkers()
        }
    },
    deactivated() {
        this.mapActive = false
        this.pendingMarkerRender = this.pendingMarkerRender || !!this.markerRenderRaf
        this.showMarkersAfterMapInteraction()
        this.cancelMarkerRender()
        this.roadSnapVersion++
    },
    methods: {
        // 获取车辆数据
        getCarList() {
            this.isLoading = true
            this.axios.post('ownCarAlarm/getTheVehicleLocation', { carCode: this.filterPlateNumber }).then(res => {
                if (res.data.code == 200) {
                    this.vehicles = res.data.data;
                    this.refreshPerformanceMode()
                    this.listScrollTop = 0
                    if (this.$refs.vehicleList) {
                        this.$refs.vehicleList.scrollTop = 0
                    }
                    // 数据获取成功后添加标记
                    if (this.mapLoaded) {
                        if (this.mapActive) {
                            this.addVehicleMarkers()
                        } else {
                            this.pendingMarkerRender = true
                        }
                    }
                    this.isLoading = false
                }
            }).catch(err => {
                this.isLoading = false
            })
        },
        // 初始化地图
        initMap() {
            // 检查是否已加载高德地图API
            if (window.AMap) {
                this.createMap()
                this.getCarList()
                return
            }

            // 加载高德地图API
            const key = 'ddc3f919a2106d25341cb70eba4b5c1e'
            const script = document.createElement('script')
            script.src = `https://webapi.amap.com/maps?v=2.0&key=${key}&plugin=AMap.Scale,AMap.ToolBar,AMap.GraspRoad,AMap.MouseTool`
            script.onload = () => {
                this.mapLoaded = true
                this.createMap()
                this.getCarList()
            }
            script.onerror = () => {
                console.error('高德地图加载失败')
                // 即使地图加载失败，也获取车辆数据
                this.getCarList()
            }
            document.head.appendChild(script)
        },

        // 创建地图实例
        createMap() {
            try {
                const reduceMapWork = this.shouldReduceMapWork()
                // 初始化地图 - 使用高德地图默认视图
                this.map = new AMap.Map('map-container', {
                    zoom: 7,
                    center: [113.665412, 34.757975],
                    resizeEnable: true,
                    dragEnable: true,
                    zoomEnable: true,
                    jogEnable: false,
                    viewMode: '2D',
                    pitch: 0,
                    rotation: 0,
                    rotateEnable: false,
                    pitchEnable: false,
                    animateEnable: !reduceMapWork,
                    keyboardEnable: false,
                    doubleClickZoom: !reduceMapWork,
                    showLabel: !reduceMapWork,
                    showIndoorMap: false,
                    features: reduceMapWork ? ['bg', 'road'] : ['bg', 'road', 'building']
                })

                if (AMap.Scale) {
                    this.map.addControl(new AMap.Scale())
                }
                if (AMap.ToolBar) {
                    this.map.addControl(new AMap.ToolBar({ position: 'RB' }))
                }
                this.bindMapInteractionEvents()
                this.initRoadSnapper()

                this.mapLoaded = true
                console.log('地图初始化成功')

            } catch (error) {
                console.error('地图初始化失败:', error)
            }
        },

        // 添加车辆标记
        refreshPerformanceMode() {
            const width = window.innerWidth || document.documentElement.clientWidth || 0
            const height = window.innerHeight || document.documentElement.clientHeight || 0
            const pixelCount = width * height
            this.performanceMode = document.documentElement.classList.contains('large-screen-perf') || pixelCount >= 3840 * 2160 || width >= 3840 || height >= 2160 || window.devicePixelRatio >= 2
        },
        resizeMapNextTick() {
            this.$nextTick(() => {
                if (this.map && typeof this.map.resize === 'function') {
                    this.map.resize()
                }
            })
        },
        toggleMapFullscreen() {
            this.isMapFullscreen = !this.isMapFullscreen
            this.resizeMapNextTick()
        },
        closeMapTools() {
            this.isMapFullscreen = false
            if (this.ruler) {
                this.ruler.close(true)
                this.rulerActive = false
            }
            if (this.map && this.trafficLayer && this.trafficVisible) {
                this.map.remove(this.trafficLayer)
            }
            this.trafficVisible = false
        },
        toggleTrafficLayer() {
            if (!this.map || !window.AMap) return
            if (!this.trafficLayer && AMap.TileLayer && AMap.TileLayer.Traffic) {
                this.trafficLayer = new AMap.TileLayer.Traffic({
                    zIndex: 10,
                    autoRefresh: true,
                    interval: 180
                })
            }
            if (!this.trafficLayer) return
            if (this.trafficVisible) {
                this.map.remove(this.trafficLayer)
                this.trafficVisible = false
            } else {
                this.map.add(this.trafficLayer)
                this.trafficVisible = true
            }
        },
        ensureMouseToolLoaded() {
            return new Promise((resolve, reject) => {
                if (!window.AMap) {
                    reject(new Error('AMap is not loaded'))
                    return
                }

                if (window.AMap.MouseTool) {
                    resolve(window.AMap.MouseTool)
                    return
                }

                if (typeof window.AMap.plugin !== 'function') {
                    reject(new Error('AMap.plugin is not available'))
                    return
                }

                const timeoutTimer = setTimeout(() => {
                    reject(new Error('AMap.MouseTool load timeout'))
                }, 8000)

                window.AMap.plugin(['AMap.MouseTool'], () => {
                    clearTimeout(timeoutTimer)
                    if (window.AMap.MouseTool) {
                        resolve(window.AMap.MouseTool)
                    } else {
                        reject(new Error('AMap.MouseTool load failed'))
                    }
                })
            })
        },
        async toggleRuler() {
            if (this.rulerLoading) return
            if (!this.map || !window.AMap) {
                if (this.$message) {
                    this.$message.warning('地图尚未加载完成，请稍后再试')
                }
                return
            }

            if (this.rulerActive) {
                if (this.ruler) {
                    this.ruler.close(true)
                }
                this.rulerActive = false
                return
            }

            this.rulerLoading = true
            try {
                await this.ensureMouseToolLoaded()
                if (!this.ruler) {
                    this.ruler = new window.AMap.MouseTool(this.map)
                }
                this.ruler.rule()
                this.rulerActive = true
                if (this.$message) {
                    this.$message.success('测距已开启，请在地图上点击选择测量点')
                }
            } catch (error) {
                console.error('[mapBoard] MouseTool load failed:', error)
                this.rulerActive = false
                if (this.$message) {
                    this.$message.error('测距工具加载失败，请刷新页面后重试')
                }
            } finally {
                this.rulerLoading = false
            }
        },
        shouldReduceMapWork(vehicleCount) {
            const count = vehicleCount === undefined ? this.vehicles.length : vehicleCount
            const width = window.innerWidth || document.documentElement.clientWidth || 0
            const height = window.innerHeight || document.documentElement.clientHeight || 0
            const pixelCount = width * height
            return this.performanceMode || pixelCount >= 3840 * 2160 || width >= 3840 || height >= 2160 || window.devicePixelRatio >= 2 || count > this.heavyVehicleThreshold
        },
        handleVehicleListScroll() {
            if (!this.shouldVirtualizeVehicles || !this.$refs.vehicleList) return
            this.listScrollTop = this.$refs.vehicleList.scrollTop
        },
        initRoadSnapper() {
            if (this.shouldReduceMapWork()) return
            if (!window.AMap || this.roadSnapper) return

            const createSnapper = () => {
                if (AMap.GraspRoad && !this.roadSnapper) {
                    this.roadSnapper = new AMap.GraspRoad()
                }
            }

            if (AMap.GraspRoad) {
                createSnapper()
                return
            }

            if (AMap.plugin) {
                AMap.plugin('AMap.GraspRoad', createSnapper)
            }
        },

        bindMapInteractionEvents() {
            if (!this.map || this.mapInteractionEventsBound) return
            this.map.on('dragstart', this.hideMarkersDuringMapInteraction)
            this.map.on('dragend', this.scheduleShowMarkersAfterMapInteraction)
            this.map.on('zoomend', this.scheduleShowMarkersAfterMapInteraction)
            this.map.on('moveend', this.scheduleShowMarkersAfterMapInteraction)
            this.mapInteractionEventsBound = true
        },

        unbindMapInteractionEvents() {
            if (!this.map || !this.mapInteractionEventsBound) return
            this.map.off('dragstart', this.hideMarkersDuringMapInteraction)
            this.map.off('dragend', this.scheduleShowMarkersAfterMapInteraction)
            this.map.off('zoomend', this.scheduleShowMarkersAfterMapInteraction)
            this.map.off('moveend', this.scheduleShowMarkersAfterMapInteraction)
            this.mapInteractionEventsBound = false
        },

        shouldHideMarkersDuringInteraction() {
            return this.shouldReduceMapWork(this.markers.length) && this.markers.length > 0 && !this.labelsLayer
        },

        hideMarkersDuringMapInteraction() {
            if (!this.shouldHideMarkersDuringInteraction() || this.markersHiddenForInteraction) return
            if (this.mapInteractionTimer) {
                clearTimeout(this.mapInteractionTimer)
                this.mapInteractionTimer = null
            }
            this.markers.forEach(marker => {
                if (marker && typeof marker.hide === 'function') {
                    marker.hide()
                }
            })
            this.markersHiddenForInteraction = true
        },

        scheduleShowMarkersAfterMapInteraction() {
            if (!this.markersHiddenForInteraction) return
            if (this.mapInteractionTimer) {
                clearTimeout(this.mapInteractionTimer)
            }
            const delay = this.shouldReduceMapWork(this.markers.length) ? 220 : 120
            this.mapInteractionTimer = setTimeout(() => {
                this.showMarkersAfterMapInteraction()
            }, delay)
        },

        showMarkersAfterMapInteraction() {
            if (this.mapInteractionTimer) {
                clearTimeout(this.mapInteractionTimer)
                this.mapInteractionTimer = null
            }
            if (!this.markersHiddenForInteraction) return
            this.markers.forEach(marker => {
                if (marker && typeof marker.show === 'function') {
                    marker.show()
                }
            })
            this.markersHiddenForInteraction = false
        },

        addVehicleMarkers() {
            if (!this.mapActive) {
                this.pendingMarkerRender = true
                return
            }
            if (!this.map) {
                console.error('地图未初始化')
                return
            }

            this.pendingMarkerRender = false
            this.cancelMarkerRender()
            this.showMarkersAfterMapInteraction()
            this.clearMarkers()

            const vehiclesWithPosition = this.vehicles.filter(vehicle => {
                const longitude = Number(vehicle.o)
                const latitude = Number(vehicle.a)
                return !Number.isNaN(longitude) && !Number.isNaN(latitude)
            }).map(vehicle => ({
                ...vehicle,
                originalPosition: [Number(vehicle.o), Number(vehicle.a)],
                mapPosition: [Number(vehicle.o), Number(vehicle.a)]
            }))

            const currentSnapVersion = ++this.roadSnapVersion
            this.snapVehiclesToRoad(vehiclesWithPosition).then(snappedVehicles => {
                if (!this.mapActive || currentSnapVersion !== this.roadSnapVersion) return
                this.renderVehicleMarkers(snappedVehicles)
            })
        },

        snapVehiclesToRoad(vehicles) {
            if (this.shouldReduceMapWork(vehicles.length) || !this.roadSnapper || vehicles.length === 0) {
                return Promise.resolve(vehicles)
            }

            const tasks = vehicles.map(vehicle => () => this.snapSingleVehicleToRoad(vehicle))
            return this.runRoadSnapTasks(tasks)
        },

        async runRoadSnapTasks(tasks) {
            const results = []
            const batchSize = 20
            for (let index = 0; index < tasks.length; index += batchSize) {
                const batch = tasks.slice(index, index + batchSize)
                const batchResults = await Promise.all(batch.map(task => task()))
                results.push(...batchResults)
                await new Promise(resolve => setTimeout(resolve, 0))
            }
            return results
        },

        snapSingleVehicleToRoad(vehicle) {
            const cacheKey = `${vehicle.id || vehicle.CARCODE || ''}:${vehicle.originalPosition[0]},${vehicle.originalPosition[1]}:${vehicle.d || ''}`
            if (this.roadSnapCache[cacheKey]) {
                return Promise.resolve({
                    ...vehicle,
                    mapPosition: this.roadSnapCache[cacheKey],
                    originalPosition: vehicle.originalPosition
                })
            }
            const trackPoints = this.createVehicleSnapTrack(vehicle)

            return new Promise(resolve => {
                this.roadSnapper.driving(trackPoints, (error, result) => {
                    if (error || !result || !Array.isArray(result.data) || result.data.length === 0) {
                        resolve(vehicle)
                        return
                    }

                    const snappedPoint = result.data[Math.floor(result.data.length / 2)] || result.data[0]
                    if (!snappedPoint || snappedPoint.x === undefined || snappedPoint.y === undefined) {
                        resolve(vehicle)
                        return
                    }

                    const mapPosition = [Number(snappedPoint.x), Number(snappedPoint.y)]
                    this.roadSnapCache[cacheKey] = mapPosition
                    resolve({
                        ...vehicle,
                        mapPosition,
                        originalPosition: vehicle.originalPosition
                    })
                })
            })
        },

        createVehicleSnapTrack(vehicle) {
            const direction = this.getVehicleDirection(vehicle.d)
            const distance = 0.00022
            const radian = direction * Math.PI / 180
            const lngOffset = Math.sin(radian) * distance
            const latOffset = Math.cos(radian) * distance
            const longitude = vehicle.originalPosition[0]
            const latitude = vehicle.originalPosition[1]
            const speed = Number(vehicle.speed) || 1

            return [
                { x: longitude - lngOffset, y: latitude - latOffset, sp: speed, ag: direction, tm: 1 },
                { x: longitude, y: latitude, sp: speed, ag: direction, tm: 2 },
                { x: longitude + lngOffset, y: latitude + latOffset, sp: speed, ag: direction, tm: 3 }
            ]
        },

        getVehicleDirection(direction) {
            const angle = Number(direction)
            return Number.isNaN(angle) ? 0 : angle
        },

        shouldUseLabelsLayer(vehicleCount) {
            return !!(window.AMap && AMap.LabelsLayer && AMap.LabelMarker && vehicleCount > 60)
        },

        renderVehicleMarkers(vehiclesWithPosition) {
            const vehiclePositionMap = {}
            const markerPositions = []
            const reduceMapWork = this.shouldReduceMapWork(vehiclesWithPosition.length)
            this.clearMarkers()
            if (this.shouldUseLabelsLayer(vehiclesWithPosition.length)) {
                this.renderVehicleLabelMarkers(vehiclesWithPosition, vehiclePositionMap, markerPositions, !reduceMapWork)
                return
            }

            const newMarkers = []
            const vehicleIcon = new AMap.Icon({
                size: new AMap.Size(reduceMapWork ? 24 : 32, reduceMapWork ? 24 : 32),
                image: require('@/assets/wz.png'),
                imageSize: new AMap.Size(reduceMapWork ? 24 : 32, reduceMapWork ? 24 : 32)
            })
            vehiclesWithPosition.forEach(vehicleWithPosition => {
                vehiclePositionMap[vehicleWithPosition.id || vehicleWithPosition.CARCODE] = vehicleWithPosition.mapPosition
                markerPositions.push(vehicleWithPosition.mapPosition)
                try {
                    const marker = new AMap.Marker({
                        position: vehicleWithPosition.mapPosition,
                        icon: vehicleIcon,
                        offset: new AMap.Pixel(reduceMapWork ? -12 : -16, reduceMapWork ? -12 : -16),
                        angle: reduceMapWork ? 0 : this.getVehicleDirection(vehicleWithPosition.d),
                        title: vehicleWithPosition.CARCODE,
                        label: reduceMapWork ? undefined : {
                            direction: 'top',
                            offset: new AMap.Pixel(0, -4),
                            content: `<div class="vehicle-map-label">${vehicleWithPosition.CARCODE}</div>`
                        },
                        extData: vehicleWithPosition
                    })

                    marker.on('click', () => {
                        this.selectVehicle(vehicleWithPosition)
                    })

                    newMarkers.push(marker)
                } catch (error) {
                    console.error(`添加车辆 ${vehicleWithPosition.CARCODE} 标记失败:`, error)
                }
            })

            this.vehiclePositionMap = vehiclePositionMap
            this.markerPositions = markerPositions
            this.markers = newMarkers
            this.markersHiddenForInteraction = false
            if (this.markers.length > 0) {
                this.addMarkersInBatches(this.markers)
            }

            if (!this.hasAdjustedMapView && this.markers.length > 0) {
                this.adjustMapView()
                this.hasAdjustedMapView = true
            }
        },

        renderVehicleLabelMarkers(vehiclesWithPosition, vehiclePositionMap, markerPositions, showText) {
            const iconSize = 24
            const labelMarkers = []
            vehiclesWithPosition.forEach(vehicleWithPosition => {
                vehiclePositionMap[vehicleWithPosition.id || vehicleWithPosition.CARCODE] = vehicleWithPosition.mapPosition
                markerPositions.push(vehicleWithPosition.mapPosition)
                try {
                    const labelMarker = new AMap.LabelMarker({
                        position: vehicleWithPosition.mapPosition,
                        title: vehicleWithPosition.CARCODE,
                        icon: {
                            image: require('@/assets/wz.png'),
                            size: [iconSize, iconSize],
                            anchor: 'center'
                        },
                        text: showText ? {
                            content: vehicleWithPosition.CARCODE || '',
                            direction: 'top',
                            offset: [0, -2],
                            style: {
                                fontSize: 12,
                                fontWeight: '700',
                                fillColor: '#ffffff',
                                padding: [2, 6, 2, 6],
                                backgroundColor: 'rgba(24, 144, 255, 0.92)',
                                borderColor: 'rgba(255, 255, 255, 0.9)',
                                borderWidth: 1
                            }
                        } : undefined,
                        extData: vehicleWithPosition
                    })
                    labelMarker.on('click', event => {
                        const data = event && event.target && event.target.getExtData ? event.target.getExtData() : vehicleWithPosition
                        this.selectVehicle(data)
                    })
                    labelMarkers.push(labelMarker)
                } catch (error) {
                    console.error(`添加车辆 ${vehicleWithPosition.CARCODE} 轻量标记失败:`, error)
                }
            })

            this.vehiclePositionMap = vehiclePositionMap
            this.markerPositions = markerPositions
            this.labelMarkers = labelMarkers
            this.markersHiddenForInteraction = false

            if (labelMarkers.length > 0) {
                this.labelsLayer = new AMap.LabelsLayer({
                    zooms: [3, 20],
                    zIndex: 120,
                    collision: true,
                    allowCollision: false
                })
                this.map.add(this.labelsLayer)
                this.labelsLayer.add(labelMarkers)
            }

            if (!this.hasAdjustedMapView && labelMarkers.length > 0) {
                this.adjustMapView()
                this.hasAdjustedMapView = true
            }
        },

        addMarkersInBatches(markers) {
            this.cancelMarkerRender()
            const reduceMapWork = this.shouldReduceMapWork(markers.length)
            if (!reduceMapWork && markers.length <= 80) {
                this.map.add(markers)
                return
            }
            var index = 0
            var batchSize = reduceMapWork ? 40 : 160
            var addNext = () => {
                if (!this.map || !this.mapActive || index >= markers.length) {
                    this.markerRenderRaf = null
                    return
                }
                this.map.add(markers.slice(index, index + batchSize))
                index += batchSize
                if (index < markers.length) {
                    this.markerRenderRaf = window.requestAnimationFrame(addNext)
                } else {
                    this.markerRenderRaf = null
                }
            }
            addNext()
        },

        cancelMarkerRender() {
            if (this.markerRenderRaf) {
                window.cancelAnimationFrame(this.markerRenderRaf)
                this.markerRenderRaf = null
            }
        },

        // 调整地图视野
        adjustMapView() {
            if (!this.map || this.markerPositions.length === 0) return

            if (this.markerPositions.length === 1) {
                this.map.setCenter(this.markerPositions[0])
                this.map.setZoom(12)
            } else if (!this.shouldReduceMapWork(this.markerPositions.length) && this.markers.length > 0) {
                this.map.setFitView(this.markers)
            } else {
                this.fitMapToPositions(this.markerPositions)
            }
        },

        fitMapToPositions(positions) {
            if (!this.map || !window.AMap || !positions || positions.length === 0) return
            var minLng = Infinity
            var minLat = Infinity
            var maxLng = -Infinity
            var maxLat = -Infinity
            positions.forEach(position => {
                var lng = Number(position && position[0])
                var lat = Number(position && position[1])
                if (!Number.isFinite(lng) || !Number.isFinite(lat)) return
                minLng = Math.min(minLng, lng)
                minLat = Math.min(minLat, lat)
                maxLng = Math.max(maxLng, lng)
                maxLat = Math.max(maxLat, lat)
            })
            if (!Number.isFinite(minLng) || !Number.isFinite(minLat) || !Number.isFinite(maxLng) || !Number.isFinite(maxLat)) return
            if (minLng === maxLng && minLat === maxLat) {
                this.map.setCenter([minLng, minLat])
                this.map.setZoom(12)
                return
            }
            var bounds = new AMap.Bounds(
                new AMap.LngLat(minLng, minLat),
                new AMap.LngLat(maxLng, maxLat)
            )
            if (typeof this.map.setBounds === 'function') {
                this.map.setBounds(bounds, false, [80, 80, 80, 80])
            } else if (this.markers.length > 0) {
                this.map.setFitView(this.markers)
            }
        },

        // 创建信息窗口内容（显示车牌号）
        createInfoWindowContent(vehicle) {
            return `
        <div class="custom-info-window">
          <div class="info-plate">${vehicle.CARCODE}</div>
          <div class="info-status">${vehicle.alarmtype}</div>
          <div class="info-address">${vehicle.newaddress}</div>
          <div class="info-speed">速度: ${vehicle.speed} km/h</div>
        </div>
      `
        },

        // 清除所有标记
        clearMarkers() {
            this.showMarkersAfterMapInteraction()
            if (this.map && this.markers.length > 0) {
                this.map.remove(this.markers)
            }
            if (this.labelsLayer) {
                if (typeof this.labelsLayer.clear === 'function') {
                    this.labelsLayer.clear()
                }
                if (this.map) {
                    this.map.remove(this.labelsLayer)
                }
                this.labelsLayer = null
            }
            this.markers = []
            this.labelMarkers = []
            this.markerPositions = []
            this.vehiclePositionMap = {}
        },

        // 选择车辆
        selectVehicle(vehicle) {
            const longitude = Number(vehicle.o)
            const latitude = Number(vehicle.a)
            const snappedPosition = this.vehiclePositionMap[vehicle.id || vehicle.CARCODE]
            const mapPosition = vehicle.mapPosition || snappedPosition || (!Number.isNaN(longitude) && !Number.isNaN(latitude) ? [longitude, latitude] : null)

            this.selectedVehicle = {
                ...vehicle,
                mapPosition
            }
            this.showVehicleDialog = true

            // 定位到选中的车辆
            if (this.map && mapPosition) {
                this.map.setCenter(mapPosition)
                this.map.setZoom(15)
            }
        },

        // 关闭弹窗
        closeDialog() {
            this.showVehicleDialog = false
            this.selectedVehicle = null
        },

        // 处理弹窗关闭事件
        handleDialogClose() {
            this.selectedVehicle = null
        },

        // 处理筛选
        handleFilter() {
            this.getCarList()
        },

        // 重置筛选
        resetFilter() {
            this.filterPlateNumber = ''
            this.getCarList()
        },
    },
    beforeDestroy() {
        if (this.mapResizeHandler) {
            window.removeEventListener('resize', this.mapResizeHandler)
            this.mapResizeHandler = null
        }
        if (this.fullscreenKeyHandler) {
            window.removeEventListener('keydown', this.fullscreenKeyHandler)
            this.fullscreenKeyHandler = null
        }
        this.closeMapTools()
        this.unbindMapInteractionEvents()
        this.showMarkersAfterMapInteraction()
        this.cancelMarkerRender()
        this.roadSnapVersion++
        this.clearMarkers()
        if (this.map) {
            this.map.destroy()
            this.map = null
        }
    }
}
</script>

<style scoped>
.dashboard-container {
    height: 100vh;
    display: flex;
    flex-direction: column;
    background: #0f1429;
}

/* 筛选区域 */
.filter-section {
    padding: 20px;
}

.filter-card {
    background: #1a223f;
    border: 1px solid #2d3a5a;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.filter-content {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 20px;
}

.filter-item {
    display: flex;
    align-items: center;
}

.filter-label {
    font-size: 14px;
    color: #a0a8c3;
    margin-right: 12px;
    white-space: nowrap;
}

.tech-input {
    width: 200px;
}

.tech-input>>>.el-input__inner {
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid #2d3a5a;
    color: #ffffff;
    border-radius: 6px;
}

.tech-input>>>.el-input__inner::placeholder {
    color: #a0a8c3;
}

.tech-input>>>.el-input__inner:focus {
    border-color: #1890ff;
}

.tech-btn {
    border-radius: 6px;
    border: 1px solid #2d3a5a;
    background: rgba(255, 255, 255, 0.1);
    color: #ffffff;
    transition: all 0.3s;
}

.tech-btn:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: #1890ff;
}

.query-btn {
    background: #1890ff;
    border-color: #1890ff;
}

.query-btn:hover {
    background: #40a9ff;
    border-color: #40a9ff;
}

/* 主内容区域 */
.main-content {
    flex: 1;
    display: flex;
    padding: 0 20px 20px;
    gap: 20px;
    overflow: hidden;
}

.map-container {
    flex: 1;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
    position: relative;
}

.map-container-fullscreen {
    position: fixed;
    inset: 0;
    z-index: 3000;
    border-radius: 0;
    box-shadow: none;
    background: #0f1429;
}

.map-container-fullscreen .map {
    min-height: 100vh;
}

.map-tool-panel {
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 20;
    display: flex;
    gap: 8px;
    padding: 8px;
    border-radius: 8px;
    background: rgba(15, 20, 41, 0.78);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.28);
}

.map-tool-btn {
    min-width: 72px;
    height: 32px;
    padding: 0 12px;
    border: 1px solid rgba(24, 144, 255, 0.55);
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.92);
    color: #0f4f8f;
    font-size: 13px;
    cursor: pointer;
}

.map-tool-btn:hover,
.map-tool-btn.active {
    background: #1890ff;
    color: #fff;
}

.map {
    width: 100%;
    height: 100%;
    min-height: 500px;
}

/* 信息面板 */
.info-panel {
    width: 450px;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 20px;
}

/* 车辆列表 */
.vehicle-list-card {
    flex: 1;
    background: #1a223f;
    border: 1px solid #2d3a5a;
    /* border-radius: 8px; */
    padding: 20px;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
}

.panel-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
    font-weight: bold;
    font-size: 16px;
    color: #fff;
}

.vehicle-count {
    color: #a0a8c3;
    font-size: 14px;
}

.list-content {
    flex: 1;
    overflow-y: auto;
    max-height: 700px;
}

.vehicle-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 15px;
    border-bottom: 1px solid #2d3a5a;
    cursor: pointer;
    transition: all 0.3s;
    margin-bottom: 8px;
    /* border-radius: 6px; */
}

.vehicle-item:hover {
    background: #2a3655;
}

.vehicle-item.active {
    background: #1890ff;
    border-left: 4px solid #ffaa00;
}

.vehicle-main {
    flex: 1;
}

.vehicle-plate {
    font-size: 16px;
    font-weight: bold;
    color: #ffffff;
    margin-bottom: 4px;
}

.vehicle-location {
    display: inline-block;
    width: 390px;
    font-size: 12px;
    color: #a0a8c3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.vehicle-status {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: 10px;
}

.status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #faad14;
}

.status-dot.normal {
    background: #00d4aa;
}

.status-dot.warning {
    background: #faad14;
}

.status-dot.offline {
    background: #ff4d4f;
}

.status-tag {
    max-width: 260px;
    border: none;
    background: rgba(255, 255, 255, 0.1);
    color: #ffffff;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* 图例 */
.map-legend {
    position: absolute;
    bottom: 20px;
    left: 20px;
    background: #1a223f;
    border: 1px solid #2d3a5a;
    padding: 15px;
    border-radius: 8px;
    z-index: 1000;
    min-width: 120px;
}

.legend-title {
    font-size: 14px;
    font-weight: bold;
    margin-bottom: 10px;
    color: #ffffff;
}

.legend-item {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
}

.legend-icon {
    width: 20px;
    height: 30px;
    margin-right: 8px;
    object-fit: contain;
}

.legend-text {
    font-size: 12px;
    color: #a0a8c3;
}

/* 弹窗样式 */
.vehicle-dialog>>>.el-dialog {
    background: #ffffff;
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.vehicle-dialog>>>.el-dialog__header {
    background: #f5f7fa;
    border-bottom: 1px solid #e4e7ed;
    padding: 20px;
    border-radius: 8px 8px 0 0;
}

.vehicle-dialog>>>.el-dialog__title {
    color: #303133;
    font-weight: bold;
}

.vehicle-dialog>>>.el-dialog__body {
    padding: 30px;
    background: #ffffff;
}

.vehicle-dialog>>>.el-dialog__footer {
    background: #f5f7fa;
    border-top: 1px solid #e4e7ed;
    padding: 20px;
    border-radius: 0 0 8px 8px;
}

.vehicle-detail {
    color: #606266;
}

.detail-section {
    margin-bottom: 30px;
}

.section-title {
    font-size: 16px;
    font-weight: bold;
    color: #1890ff;
    margin-bottom: 15px;
    padding-bottom: 8px;
    border-bottom: 1px solid #e4e7ed;
}

.detail-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 15px;
}

.detail-item {
    display: flex;
    align-items: center;
    padding: 8px 0;
}

.detail-label {
    color: #909399;
    font-size: 14px;
    min-width: 80px;
}

.detail-value {
    color: #303133;
    font-size: 14px;
    flex: 1;
}

.dialog-footer .tech-btn {
    color: #606266;
}

.dialog-footer .tech-btn:first-child {
    background: transparent;
    border-color: #dcdfe6;
}

.dialog-footer .tech-btn:first-child:hover {
    background: #f5f7fa;
    border-color: #c0c4cc;
}

.dialog-footer .tech-btn:last-child {
    background: #1890ff;
    border-color: #1890ff;
    color: #ffffff;
}

.dialog-footer .tech-btn:last-child:hover {
    background: #40a9ff;
    border-color: #40a9ff;
}

/* 自定义信息窗口样式 */
.custom-info-window {
    background: #1a223f;
    border: 1px solid #2d3a5a;
    border-radius: 6px;
    padding: 8px 12px;
    color: #ffffff;
    min-width: 180px;
}

.info-plate {
    font-weight: bold;
    font-size: 14px;
    margin-bottom: 4px;
}

.info-status {
    font-size: 12px;
    padding: 2px 6px;
    border-radius: 4px;
    display: inline-block;
    background: #00d4aa;
    color: white;
    margin-bottom: 4px;
}

.info-address {
    font-size: 12px;
    color: #a0a8c3;
    margin-bottom: 2px;
}

.info-speed {
    font-size: 12px;
    color: #a0a8c3;
}

/* 滚动条样式 */
.list-content::-webkit-scrollbar {
    width: 6px;
}

.list-content::-webkit-scrollbar-thumb {
    background: #2d3a5a;
    border-radius: 3px;
}

.list-content::-webkit-scrollbar-thumb:hover {
    background: #1890ff;
}
.vehicle-info {
    display: flex;
}
.loading-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.7);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 99;
}

.loading-spinner {
    text-align: center;
    color: #fff;
}

.loading-spinner i {
    font-size: 40px;
    margin-bottom: 10px;
}

.dashboard-container {
    height: calc(100vh - 126px);
    min-height: 720px;
    padding: 18px;
    box-sizing: border-box;
    background:
        radial-gradient(circle at 10% 0%, rgba(23, 139, 255, 0.12) 0, rgba(23, 139, 255, 0) 30%),
        linear-gradient(135deg, #eef6ff 0%, #f7fbff 48%, #edf5fc 100%);
    color: var(--fszs-text);
}

.filter-section {
    padding: 0 0 16px;
}

.filter-card {
    padding: 16px 18px;
    border: 1px solid rgba(220, 230, 242, 0.96);
    border-radius: var(--fszs-radius-lg);
    background: rgba(255, 255, 255, 0.96);
    box-shadow: var(--fszs-shadow-sm);
}

.filter-content {
    gap: 12px 16px;
}

.filter-item {
    gap: 10px;
}

.filter-label {
    margin-right: 0;
    color: var(--fszs-text-muted);
    font-weight: 600;
}

.tech-input {
    width: 260px;
}

.tech-input>>>.el-input__inner {
    height: 38px;
    line-height: 38px;
    border-color: var(--fszs-border);
    border-radius: 9px;
    background: #f8fbff;
    color: var(--fszs-text);
}

.tech-input>>>.el-input__inner::placeholder {
    color: var(--fszs-text-subtle);
}

.tech-input>>>.el-input__inner:focus {
    border-color: var(--fszs-primary);
    background: #ffffff;
    box-shadow: var(--fszs-focus);
}

.tech-btn {
    height: 38px;
    padding: 0 16px;
    border-radius: 9px;
    border-color: var(--fszs-border);
    background: #ffffff;
    color: var(--fszs-text-muted);
    box-shadow: none;
}

.tech-btn:hover {
    border-color: var(--fszs-primary);
    background: var(--fszs-primary-soft);
    color: var(--fszs-primary);
}

.query-btn,
.dialog-footer .tech-btn:last-child {
    border-color: var(--fszs-primary);
    background: linear-gradient(135deg, var(--fszs-primary) 0%, var(--fszs-primary-strong) 100%);
    color: #ffffff;
    box-shadow: 0 8px 18px rgba(23, 139, 255, 0.24);
}

.query-btn:hover,
.dialog-footer .tech-btn:last-child:hover {
    border-color: var(--fszs-primary-hover);
    background: linear-gradient(135deg, var(--fszs-primary-hover) 0%, var(--fszs-primary) 100%);
    color: #ffffff;
}

.main-content {
    padding: 0;
    gap: 16px;
}

.map-container {
    border: 1px solid rgba(220, 230, 242, 0.96);
    border-radius: var(--fszs-radius-lg);
    background: #ffffff;
    box-shadow: var(--fszs-shadow-md);
}

.map {
    min-height: 560px;
}

.info-panel {
    width: 390px;
    gap: 16px;
}

.vehicle-list-card {
    padding: 16px;
    border: 1px solid rgba(220, 230, 242, 0.96);
    border-radius: var(--fszs-radius-lg);
    background: rgba(255, 255, 255, 0.97);
    box-shadow: var(--fszs-shadow-md);
}

.panel-header {
    margin-bottom: 12px;
    color: var(--fszs-text-strong);
    font-size: 16px;
}

.vehicle-count {
    color: var(--fszs-primary);
    font-weight: 700;
}

.list-content {
    max-height: none;
    padding-right: 4px;
}

.vehicle-item {
    align-items: flex-start;
    padding: 12px 14px;
    margin-bottom: 10px;
    border: 1px solid var(--fszs-border-soft);
    border-radius: 12px;
    background: #f8fbff;
    box-shadow: none;
}

.vehicle-item:hover {
    border-color: rgba(23, 139, 255, 0.28);
    background: #ffffff;
    box-shadow: var(--fszs-shadow-sm);
}

.vehicle-item.active {
    border-color: rgba(23, 139, 255, 0.34);
    border-left: 4px solid var(--fszs-primary);
    background: linear-gradient(135deg, rgba(23, 139, 255, 0.14) 0%, rgba(23, 139, 255, 0.06) 100%);
}

.vehicle-info {
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}

.vehicle-plate {
    color: var(--fszs-text-strong);
}

.vehicle-location {
    width: 320px;
    margin-top: 5px;
    color: var(--fszs-text-muted);
    line-height: 1.5;
}

.status-tag {
    max-width: 170px;
    border: 1px solid rgba(23, 139, 255, 0.18);
    background: var(--fszs-primary-soft);
    color: var(--fszs-primary);
}

.map-legend {
    border: 1px solid rgba(220, 230, 242, 0.96);
    background: rgba(255, 255, 255, 0.94);
    box-shadow: var(--fszs-shadow-sm);
}

.legend-title,
.info-plate {
    color: var(--fszs-text-strong);
}

.legend-text,
.info-address,
.info-speed {
    color: var(--fszs-text-muted);
}

.custom-info-window {
    border-color: rgba(220, 230, 242, 0.96);
    background: #ffffff;
    color: var(--fszs-text);
    box-shadow: var(--fszs-shadow-md);
}

.info-status {
    background: var(--fszs-primary);
}

.vehicle-dialog>>>.el-dialog {
    border-radius: var(--fszs-radius-lg);
    box-shadow: var(--fszs-shadow-lg);
}

.vehicle-dialog>>>.el-dialog__header,
.vehicle-dialog>>>.el-dialog__footer {
    background: #f8fbff;
    border-color: var(--fszs-border-soft);
}

.vehicle-dialog>>>.el-dialog__title {
    color: var(--fszs-text-strong);
}

.vehicle-dialog>>>.el-dialog__body {
    padding: 24px;
}

.detail-section {
    margin-bottom: 20px;
    padding: 16px;
    border: 1px solid var(--fszs-border-soft);
    border-radius: var(--fszs-radius-md);
    background: #fbfdff;
}

.section-title {
    color: var(--fszs-primary);
    border-bottom-color: var(--fszs-border-soft);
}

.detail-grid {
    gap: 12px 16px;
}

.detail-label {
    color: var(--fszs-text-muted);
}

.detail-value {
    color: var(--fszs-text-strong);
}

.list-content::-webkit-scrollbar-thumb {
    background: rgba(23, 139, 255, 0.22);
}

@media screen and (max-width: 1280px) {
    .dashboard-container {
        min-height: 760px;
        padding: 12px;
    }

    .main-content {
        flex-direction: column;
        overflow: auto;
    }

    .info-panel {
        width: 100%;
        min-height: 280px;
    }

    .vehicle-location {
        width: calc(100vw - 96px);
    }
}

@media screen and (max-width: 768px) {
    .dashboard-container {
        height: auto;
        min-height: 100vh;
        padding: 8px;
        overflow-x: hidden;
    }

    .filter-section {
        padding-bottom: 10px;
    }

    .filter-card {
        padding: 12px;
    }

    .filter-content,
    .filter-item {
        width: 100%;
        align-items: stretch;
    }

    .filter-item {
        flex-wrap: wrap;
        gap: 8px;
    }

    .filter-label {
        width: 100%;
        white-space: normal;
    }

    .tech-input,
    .filter-item>>>.el-input,
    .filter-item>>>.el-button {
        width: 100%;
    }

    .main-content {
        flex-direction: column;
        gap: 10px;
        overflow: visible;
    }

    .map-container {
        width: 100%;
        flex: none;
    }

    .map {
        height: 360px;
        min-height: 360px;
    }

    .info-panel {
        width: 100%;
        min-height: 0;
        height: auto;
    }

    .vehicle-list-card {
        padding: 12px;
    }

    .list-content {
        max-height: none;
        overflow-y: visible;
    }

    .vehicle-item {
        padding: 10px;
    }

    .vehicle-info {
        min-width: 0;
        flex-wrap: wrap;
    }

    .vehicle-main {
        min-width: 0;
    }

    .vehicle-location {
        display: block;
        width: 100%;
        max-width: 100%;
    }

    .status-tag {
        max-width: 100%;
    }

    .vehicle-dialog>>>.el-dialog__body {
        padding: 16px;
    }

    .detail-grid {
        grid-template-columns: 1fr;
    }

    .detail-item {
        align-items: flex-start;
        flex-direction: column;
        gap: 4px;
    }

    .detail-label {
        min-width: 0;
    }
}
</style>
<style>
.vehicle-map-label {
	padding: 3px 8px;
	border: 1px solid rgba(255, 255, 255, 0.92);
	border-radius: 4px;
	background: rgba(24, 144, 255, 0.95);
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.28);
	color: #ffffff;
	font-size: 12px;
	font-weight: 700;
	line-height: 1.2;
	white-space: nowrap;
}

.data-board-map-board-dialog {
	border-radius: var(--fszs-radius-lg);
	box-shadow: var(--fszs-shadow-lg);
	overflow: hidden;
}

.data-board-map-board-dialog .el-dialog__header {
	height: auto;
	line-height: normal;
	padding: 18px 54px 15px 24px;
	vertical-align: middle;
	background: linear-gradient(90deg, rgba(23, 139, 255, 0.10) 0%, rgba(23, 139, 255, 0) 42%), linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
	border-bottom: 1px solid var(--fszs-border-soft);
	color: var(--fszs-text-strong);
}

.data-board-map-board-dialog .el-dialog__title {
	font-size: 18px;
	font-weight: 700;
	color: #102033 !important;
}

.data-board-map-board-dialog .el-dialog__headerbtn {
	top: 12px;
	right: 16px;
	border-radius: 50%;
}

.data-board-map-board-dialog .el-dialog__headerbtn .el-dialog__close {
	vertical-align: middle;
	font-size: 18px;
	color: #55708b !important;
}

@media screen and (max-width: 768px) {
	.data-board-map-board-dialog {
		width: calc(100vw - 24px) !important;
		max-width: calc(100vw - 24px);
		margin-top: 8vh !important;
	}

	.data-board-map-board-dialog .el-dialog__header {
		padding: 14px 44px 12px 16px;
	}

	.data-board-map-board-dialog .el-dialog__body {
		max-height: calc(100vh - 190px);
		overflow-y: auto;
	}
}
</style>
